import { ApolloClient, ApolloQueryResult } from '@apollo/client';
import { TypedDocumentNode } from '@graphql-typed-document-node/core';
import { unsafeCoerce } from 'fp-ts/lib/function';
import { GraphQLError } from 'graphql';
import { PreloadJob } from '../../../UnicronApp/preloader/PreloadJob';
import { HannibalSlot } from './HannibalCacheKey';
import { hannibalClientForPreload } from './HannibalProvider';

/** We use a WeakMap so that multiple queries for the same job can share the same client, but
 * the client will be garbage-collected after the job is complete.
 *
 * This is necessary to ensure that queries can run in parallel while sharing an Apollo cache.
 */
const ClientPerJob = new WeakMap<PreloadJob, ApolloClient<unknown>>();

const getClientForJob = (job: PreloadJob) => {
  const client = ClientPerJob.get(job);

  if (client) {
    return client;
  } else {
    const newClient = hannibalClientForPreload(job);
    ClientPerJob.set(job, newClient);
    return newClient;
  }
};

export const preloadHannibalQuery = async <ResultShape, Variables>(
  job: PreloadJob,
  query: TypedDocumentNode<ResultShape, Variables>,
  variables: Variables,
): Promise<PreloadResult<ResultShape>> =>
  job.services.logger.profile('Preloading hannibal query', async () => {
    const client = getClientForJob(job);

    try {
      return await client.query<ResultShape, Variables>({
        query,
        variables,
      });
    } catch (error) {
      if (error instanceof GraphQLError) {
        return { errors: [error] };
      }

      return unsafeCoerce({ errors: [error] });
    } finally {
      job.services.cache.set(HannibalSlot, client.extract());
    }
  });

type PreloadResult<T> = Partial<ApolloQueryResult<T>>;
